import React, { useEffect, useState, useMemo } from "react";
import {
  Grid,
  Paper,
  Typography,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  IconButton,
  Tabs,
  Tab,
  Box,
  Fab,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  Menu,
  MenuItem,
  LinearProgress,
  Checkbox,
  Snackbar,
  Alert,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import axios from "axios";

/* ---------------- TAB PANELS ---------------- */

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

function NestedTabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

/* ---------------- EMPTY COMPANY ---------------- */

const emptyCompany = {
  name: "",
  party_short_code: "",
  // country_code: "",
  // description: "",
  // location: "",
  // address: "",
  // city: "",
  // logo: "",
  // phone: "",
  // version: "",
  // handshake_endpoint: "",
  // is_handshake_done: false,
  // is_cpo: true,
  // is_emsp: true,
};

/* ================= COMPONENT ================= */

const OCPICompaniesPage = () => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);

  /* TABS */
  const [detailTab, setDetailTab] = useState(0);
  const [locationTab, setLocationTab] = useState(0);
  const [tariffTab, setTariffTab] = useState(0);
  const [cdrTab, setCdrTab] = useState(0);
  const [sessionTab, setSessionTab] = useState(0);

  /* DATA */
  const [locationsShared, setLocationsShared] = useState([]);
  const [locationsReceived, setLocationsReceived] = useState([]);
  const [tariffsShared, setTariffsShared] = useState([]);
  const [tariffsReceived, setTariffsReceived] = useState([]);
  const [cdrShared, setCdrShared] = useState([]);
  const [cdrReceived, setCdrReceived] = useState([]);
  const [sessionsShared, setSessionsShared] = useState([]);
  const [sessionsReceived, setSessionsReceived] = useState([]);

  /* DIALOGS */
  const [openEdit, setOpenEdit] = useState(false);
  const [openAdd, setOpenAdd] = useState(false);
  const [editData, setEditData] = useState({});
  const [addData, setAddData] = useState(emptyCompany);

  const [activeSessions, setActiveSessions] = useState({});

  const token = localStorage.getItem("token");
  const config = { headers: { Authorization: `Bearer ${token}` } };

  const [handshakeLoading, setHandshakeLoading] = useState(false);
  const [openHandshakeDialog, setOpenHandshakeDialog] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuCompanyId, setMenuCompanyId] = useState(null);

  const [isTokenSaved, setIsTokenSaved] = useState(false);
  const [savingToken, setSavingToken] = useState(false);

  const [openEndpointModal, setOpenEndpointModal] = useState(false);
  const [endpointData, setEndpointData] = useState(null);
  const [endpointTitle, setEndpointTitle] = useState("");

  const [handshakeForm, setHandshakeForm] = useState({
    endpoint: "",
    url: `${process.env.REACT_APP_OCPI_URL}/v1/ocpi/versions`,
    auth_token: "",
    payload_token: "",
    party_id: "TCH",
    country_code: "JP",
    name: "Terra Charge",
    is_cpo: true,
    is_emsp: true,
  });

  const [selectedLocations, setSelectedLocations] = useState([]);

  const [openPushAllDialog, setOpenPushAllDialog] = useState(false);
  const [pushDelay, setPushDelay] = useState(1);

  const [bulkPushLoading, setBulkPushLoading] = useState(false);
  const [bulkPushProgress, setBulkPushProgress] = useState({
    total: 0,
    completed: 0,
  });
  const [selectedCDRs, setSelectedCDRs] = useState([]);
  const [selectedSessions, setSelectedSessions] = useState([]);

  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success", // "success" | "error" | "info" | "warning"
  });

  // Helper function to show toast
  const showToast = (message, severity = "success") => {
    setToast({
      open: true,
      message,
      severity,
    });
  };

  const handleCloseToast = () => {
    setToast({ ...toast, open: false });
  };
  /* ---------------- FETCH COMPANIES ---------------- */

  useEffect(() => {
    fetchCompanies();
  }, []);
  const callOCPIApi = async ({
    url,
    method = "GET",
    payload = null,
    headers = {},
  }) => {
    try {
      const isInternal = url.includes("/v1/ocpi/dashboard");

      if (isInternal) {
        // ✅ Only your backend APIs
        return await axios({
          url,
          method,
          data: payload,
          headers,
        });
      }

      // 🔥 EVERYTHING ELSE goes via proxy
      return await axios.post(
        `${process.env.REACT_APP_OCPI_URL}/v1/ocpi/dashboard/call/api/`,
        {
          url,
          method,
          payload,
          headers,
        },
        config,
      );
    } catch (error) {
      console.error("OCPI API Error:", error);
      throw error;
    }
  };

  const handleSelectCDR = (cdrId) => {
    setSelectedCDRs((prev) => {
      if (prev.includes(cdrId)) {
        return prev.filter((id) => id !== cdrId);
      }
      return [...prev, cdrId];
    });
  };

  const handleSelectAllCDRs = () => {
    const allSelected = selectedCDRs.length === cdrReceived.length;
    if (allSelected) {
      setSelectedCDRs([]);
    } else {
      const allIds = cdrReceived.map((cdr) => cdr.id);
      setSelectedCDRs(allIds);
    }
  };

  // For Sessions
  const handleSelectSession = (sessionId) => {
    setSelectedSessions((prev) => {
      if (prev.includes(sessionId)) {
        return prev.filter((id) => id !== sessionId);
      }
      return [...prev, sessionId];
    });
  };

  const handleSelectAllSessions = () => {
    const allSelected = selectedSessions.length === sessionsReceived.length;
    if (allSelected) {
      setSelectedSessions([]);
    } else {
      const allIds = sessionsReceived.map((session) => session.id);
      setSelectedSessions(allIds);
    }
  };

  // Push CDR (Corrected - only CDR ID in URL)
  const handlePushCDR = async (cdr) => {
    try {
      if (!selectedCompany?.all_endpoints?.endpoints) {
        showToast("Endpoints not available ❌", "error");
        return;
      }

      const endpointObj = selectedCompany.all_endpoints.endpoints.find(
        (ep) => ep.identifier === "cdrs" && ep.role === "RECEIVER",
      );

      if (!endpointObj?.url) {
        showToast("CDRs endpoint not found ❌", "error");
        return;
      }

      if (!selectedCompany?.token_b) {
        showToast("Token B missing ❌", "error");
        return;
      }

      const url = `${endpointObj.url}`;
      const encodedToken = btoa(selectedCompany.token_b);

      await callOCPIApi({
        url,
        method: "POST",
        payload: cdr,
        headers: {
          Authorization: `Token ${encodedToken}`,
          "Content-Type": "application/json",
        },
      });

      showToast("CDR pushed successfully ✅", "success");
    } catch (error) {
      console.error(error);
      showToast("Failed to push CDR ❌", "error");
    }
  };

  // Push Session
  const handlePushSession = async (session) => {
    try {
      if (!selectedCompany?.all_endpoints?.endpoints) {
        showToast("Endpoints not available ❌", "error");
        return;
      }

      const endpointObj = selectedCompany.all_endpoints.endpoints.find(
        (ep) => ep.identifier === "sessions" && ep.role === "RECEIVER",
      );

      if (!endpointObj?.url) {
        showToast("Sessions endpoint not found ❌", "error");
        return;
      }

      if (!selectedCompany?.token_b) {
        showToast("Token B missing ❌", "error");
        return;
      }

      const url = `${endpointObj.url}/${session.country_code}/${session.party_id}/${session.id}`;
      const encodedToken = btoa(selectedCompany.token_b);

      await callOCPIApi({
        url,
        method: "PUT",
        payload: session,
        headers: {
          Authorization: `Token ${encodedToken}`,
          "Content-Type": "application/json",
        },
      });

      showToast("Session pushed successfully ✅", "success");
    } catch (error) {
      console.error(error);
      showToast("Failed to push session ❌", "error");
    }
  };

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const handleSelectLocation = (locationId) => {
    setSelectedLocations((prev) => {
      if (prev.includes(locationId)) {
        return prev.filter((id) => id !== locationId);
      }

      return [...prev, locationId];
    });
  };

  const handleSelectAllLocations = () => {
    const allSelected = selectedLocations.length === locationsShared.length;

    if (allSelected) {
      setSelectedLocations([]);
    } else {
      const allIds = locationsShared.map((loc) => loc.id);
      setSelectedLocations(allIds);
    }
  };

  const handlePushAllLocations = async () => {
    try {
      setBulkPushLoading(true);

      const selectedRows = locationsShared.filter((loc) =>
        selectedLocations.includes(loc.id),
      );

      setBulkPushProgress({
        total: selectedRows.length,
        completed: 0,
      });

      for (let i = 0; i < selectedRows.length; i++) {
        const row = selectedRows[i];

        try {
          await handlePushLocation(row);
        } catch (err) {
          console.error("Push failed:", row.id, err);
        }

        setBulkPushProgress({
          total: selectedRows.length,
          completed: i + 1,
        });

        if (i < selectedRows.length - 1) {
          await sleep(pushDelay * 1000);
        }
      }

      showToast("All selected locations pushed successfully ✅", "success");
      setOpenPushAllDialog(false);
    } catch (err) {
      console.error(err);
      showToast("Bulk push failed ❌", "error");
    } finally {
      setBulkPushLoading(false);
    }
  };

  const handlePushLocation = async (location) => {
    try {
      if (!selectedCompany?.all_endpoints?.endpoints) {
        showToast("Endpoints not available ❌", "error");
        return;
      }

      // 🔥 Get correct endpoint dynamically
      const endpointObj = selectedCompany.all_endpoints.endpoints.find(
        (ep) => ep.identifier === "locations" && ep.role === "RECEIVER", // push TO receiver
      );

      if (!endpointObj?.url) {
        showToast("Locations endpoint not found ❌", "error");
        return;
      }

      if (!selectedCompany?.token_b) {
        showToast("Token B missing ❌", "error");
        return;
      }

      // 🔥 Build final URL
      const url = `${endpointObj.url}/${location.country_code}/${location.party_id}/${location.id}`;

      const encodedToken = btoa(selectedCompany.token_b);

      setLoading(true);
      await callOCPIApi({
        url,
        method: "PUT",
        payload: location,
        headers: {
          Authorization: `Token ${encodedToken}`,
          "Content-Type": "application/json",
        },
      });

      showToast("Location pushed successfully ✅", "success");
    } catch (error) {
      console.error(error);
      showToast("Failed to push location ❌", "error");
    } finally {
      setLoading(false);
    }
  };
  const handleFetchOCPIEndpoint = async (identifier, role) => {
    try {
      if (!selectedCompany?.all_endpoints?.endpoints) {
        showToast("Endpoints not available ❌", "error");
        return;
      }

      const endpointObj = selectedCompany.all_endpoints.endpoints.find(
        (ep) => ep.identifier === identifier && ep.role === role,
      );

      if (!endpointObj?.url) {
        showToast(`${identifier} endpoint not found ❌`, "error");
        return;
      }

      if (!selectedCompany?.token_b) {
        showToast("Token B missing ❌", "error");
        return;
      }

      setLoading(true);

      const encodedToken = btoa(selectedCompany.token_b);

      const response = await callOCPIApi({
        url: endpointObj.url,
        method: "GET",
        headers: {
          Authorization: `Token ${encodedToken}`,
        },
      });

      const apiData = response?.data?.data || response?.data;
      if (apiData?.status_code && apiData.status_code !== 1000) {
        showToast("Failed ❌", "error");
        return;
      }

      setEndpointTitle(identifier.toUpperCase());
      setEndpointData(response.data.data);
      setOpenEndpointModal(true);
    } catch (err) {
      console.error(err);
      showToast("Error fetching endpoint ❌", "error");
    } finally {
      setLoading(false);
    }
  };
  const toBase64 = (str) => {
    try {
      return btoa(str);
    } catch (e) {
      return "";
    }
  };

  const generateUUID = () => {
    return crypto.randomUUID(); // modern browsers
  };

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_OCPI_URL}/v1/ocpi/dashboard/parties/`,
        config,
      );
      const data = res.data.data;
      setCompanies(data);
      if (data.length > 0) setSelectedCompany(data[0]);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleHandshake = async () => {
    if (!selectedCompany) return;

    const isUpdate = selectedCompany.is_handshake_done;
    setIsTokenSaved(false);

    if (!isUpdate) {
      setHandshakeForm({
        endpoint: "",
        auth_token: "",
        payload_token: selectedCompany?.token_a || "",
        url: `${process.env.REACT_APP_OCPI_URL}/v1/ocpi/versions`,
        party_id: "TCH",
        country_code: "JP",
        name: "Terra Charge",
        is_cpo: true,
        is_emsp: true,
      });

      setOpenHandshakeDialog(true);
      return;
    }

    // ✅ UPDATE FLOW (ONLY PREPARE UI)
    const credentialsEndpoint = selectedCompany?.all_endpoints?.endpoints?.find(
      (ep) => ep.identifier === "credentials" && ep.role === "RECEIVER",
    );

    if (!credentialsEndpoint?.url) {
      showToast("Credentials endpoint not found ❌", "error");
      return;
    }

    setHandshakeForm({
      endpoint: credentialsEndpoint.url,
      auth_token: "",
      payload_token: selectedCompany?.token_c || "",
      url: `${process.env.REACT_APP_OCPI_URL}/v1/ocpi/versions`,
      party_id: "TCH",
      country_code: "JP",
      name: "Terra Charge",
      is_cpo: selectedCompany?.is_cpo,
      is_emsp: selectedCompany?.is_emsp,
    });

    setOpenHandshakeDialog(true);
  };

  const handleSaveTokenC = async () => {
    try {
      if (!handshakeForm.payload_token) {
        showToast("Generate UUID first ❌", "error");
        return;
      }

      setSavingToken(true);

      await axios.put(
        `${process.env.REACT_APP_OCPI_URL}/v1/ocpi/dashboard/party/${selectedCompany.id}/`,
        {
          token_c: handshakeForm.payload_token,
        },
        config,
      );

      setIsTokenSaved(true);
      showToast("Token C saved successfully ✅", "success");
    } catch (err) {
      console.error(err);
      showToast("Failed to save Token C ❌", "error");
    } finally {
      setSavingToken(false);
    }
  };

  const handleFetchEndpoints = async () => {
    try {
      console.log("-----------selectedCompany--------", selectedCompany);
      if (!selectedCompany?.handshake_endpoint) {
        showToast("Handshake endpoint missing ❌", "error");
        return;
      }

      if (!selectedCompany?.token_b) {
        showToast("Token B missing ❌", "error");
        return;
      }

      setLoading(true);

      const encodedToken = btoa(selectedCompany.token_b);

      /* -------- STEP 1: GET ENDPOINTS DIRECTLY -------- */
      const response = await callOCPIApi({
        url: selectedCompany.handshake_endpoint,
        method: "GET",
        headers: {
          Authorization: `Token ${encodedToken}`,
        },
      });

      console.log("Endpoints Response:", response);

      if (response?.data?.status_code !== 1000) {
        showToast("Failed to fetch endpoints ❌", "error");
        return;
      }

      const endpointsData = response?.data?.data;

      /*all endoints data*/

      const all_endpoint_response = await callOCPIApi({
        url: endpointsData[0]?.url,
        method: "GET",
        headers: {
          Authorization: `Token ${encodedToken}`,
        },
      });

      if (all_endpoint_response?.data?.status_code !== 1000) {
        showToast("Failed to fetch all endpoints ❌", "error");
        return;
      }

      /*all endpoints data*/

      if (!all_endpoint_response?.data?.data?.endpoints) {
        showToast("Invalid endpoints data in response ❌", "error");
        return;
      }

      /* -------- STEP 2: SAVE TO BACKEND -------- */
      const updatePayload = {
        version: all_endpoint_response?.data?.data?.version,
        all_endpoints: all_endpoint_response?.data?.data,
      };

      await axios.put(
        `${process.env.REACT_APP_OCPI_URL}/v1/ocpi/dashboard/party/${selectedCompany.id}/`,
        updatePayload,
        config,
      );

      showToast("Endpoints synced successfully ✅", "success");

      await fetchCompanies();
      await handleLoadCompany(selectedCompany.id);
    } catch (error) {
      console.error(error);
      showToast("Error fetching endpoints ❌", "error");
    } finally {
      setLoading(false);
    }
  };

  const executeHandshake = async ({ endpoint, isUpdate }) => {
    try {
      if (!selectedCompany && isUpdate) return;

      setHandshakeLoading(true);

      let url = endpoint;
      /* ================= BUILD ROLES ================= */
      let roles = [];

      if (isUpdate) {
        // FROM COMPANY
        if (selectedCompany.is_cpo) {
          roles.push({
            role: "CPO",
            party_id: "TCH",
            country_code: "JP",
            business_details: {
              name: "Terra Charge",
            },
          });
        }

        if (selectedCompany.is_emsp) {
          roles.push({
            role: "EMSP",
            party_id: "TCH",
            country_code: "JP",
            business_details: {
              name: "Terra Charge",
            },
          });
        }
      } else {
        // FROM DIALOG (POST)
        if (handshakeForm.is_cpo) {
          roles.push({
            role: "CPO",
            party_id: handshakeForm.party_id,
            country_code: handshakeForm.country_code,
            business_details: {
              name: handshakeForm.name,
            },
          });
        }

        if (handshakeForm.is_emsp) {
          roles.push({
            role: "EMSP",
            party_id: handshakeForm.party_id,
            country_code: handshakeForm.country_code,
            business_details: {
              name: handshakeForm.name,
            },
          });
        }
      }

      if (roles.length === 0) {
        showToast("Select at least one role (CPO or EMSP) ❌", "error");
        return;
      }

      /* ================= PAYLOAD ================= */
      const payload = {
        token: handshakeForm.payload_token,
        url: handshakeForm.url,
        roles,
      };

      /* ================= TOKEN ================= */
      const rawToken = isUpdate
        ? selectedCompany.token_b
        : handshakeForm.auth_token;

      if (!rawToken) {
        showToast("Authorization token missing ❌", "error");
        return;
      }

      const encodedToken = btoa(rawToken);

      /* ================= METHOD ================= */
      const method = isUpdate ? "put" : "post";

      console.log("HANDSHAKE FORM:", handshakeForm);

      if (!handshakeForm.payload_token) {
        showToast("Payload token (Token B) missing ❌", "error");
        return;
      }
      if (!handshakeForm.url && !isUpdate) {
        showToast("Payload URL is required ❌", "error");
        return;
      }

      if (isUpdate) {
        const credentialsEndpoint =
          selectedCompany?.all_endpoints?.endpoints?.find(
            (ep) => ep.identifier === "credentials" && ep.role === "RECEIVER",
          );

        if (!credentialsEndpoint?.url) {
          showToast("Credentials endpoint not found ❌", "error");
          return;
        }

        url = credentialsEndpoint.url;
      }

      const response = await callOCPIApi({
        url: url,
        method: isUpdate ? "PUT" : "POST",
        payload: payload,
        headers: {
          Authorization: `Token ${encodedToken}`,
          "Content-Type": "application/json",
        },
      });

      /* ================= RESPONSE ================= */
      if (response?.data?.status_code === 1000) {
        showToast(
          isUpdate
            ? "Handshake updated successfully ✅"
            : "Handshake completed successfully ✅",
          "success",
        );

        const responseData = response?.data?.data;

        // Extract roles
        const roles = responseData?.roles || [];

        const is_cpo = roles.some((r) => r.role === "CPO");
        const is_emsp = roles.some((r) => r.role === "EMSP");

        // Take first role for shared fields
        const firstRole = roles[0] || {};

        const updatePayload = {
          token_b: responseData?.token,
          party_short_code: firstRole?.party_id,
          country_code: firstRole?.country_code,
          name: firstRole?.business_details?.name,
          is_cpo,
          is_emsp,
          is_handshake_done: true,
          handshake_endpoint: responseData?.url,
        };

        // 🔄 Update backend
        try {
          await axios.put(
            `${process.env.REACT_APP_OCPI_URL}/v1/ocpi/dashboard/party/${selectedCompany.id}/`,
            updatePayload,
            config,
          );

          showToast("Handshake data saved successfully ✅", "success");
        } catch (err) {
          console.error(err);
          showToast("Failed to save handshake data ❌", "error");
        }

        setOpenHandshakeDialog(false);

        await fetchCompanies();
        if (selectedCompany?.id) {
          await handleLoadCompany(selectedCompany.id);
        }
      } else {
        showToast("Handshake failed ❌", "error");
      }
    } catch (error) {
      console.error("Handshake Error:", error);
      showToast("Error during handshake", "error");
    } finally {
      setHandshakeLoading(false);
    }
  };

  const handleMenuOpen = (event, companyId) => {
    event.stopPropagation(); // prevent row click
    setAnchorEl(event.currentTarget);
    setMenuCompanyId(companyId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuCompanyId(null);
  };

  const handleDelete = async () => {
    handleMenuClose();

    const confirmDelete = window.confirm("Are you sure you want to delete?");
    if (!confirmDelete) return;

    try {
      console.log("Deleting company:", menuCompanyId);

      await axios.delete(
        `${process.env.REACT_APP_OCPI_URL}/v1/ocpi/dashboard/party/${menuCompanyId}/`,
        config,
      );

      // 🔄 Refresh list
      await fetchCompanies();

      // 🧠 Reset selected company if deleted
      if (selectedCompany?.id === menuCompanyId) {
        setSelectedCompany(null);
      }

      showToast("Company deleted successfully ✅", "success");
    } catch (error) {
      console.error("Delete Error:", error);
      showToast("Failed to delete company ❌", "error");
    }
  };

  /* ---------------- FILTER ---------------- */

  const filteredCompanies = useMemo(() => {
    return companies.filter((c) =>
      `${c.name} ${c.country_code || ""} ${c.party_short_code || ""}`
        .toLowerCase()
        .includes(searchTerm.toLowerCase()),
    );
  }, [companies, searchTerm]);

  /* ---------------- UPDATE ---------------- */

  const handleUpdate = async () => {
    try {
      const {
        id,
        token_a,
        token_b,
        token_c,
        all_endpoints,
        extra,
        created_at,
        updated_at,
        ...payload
      } = editData;

      await axios.put(
        `${process.env.REACT_APP_BASE_ENDPOINT}/ocpi/parties/${id}/`,
        payload,
        config,
      );

      setOpenEdit(false);
      fetchCompanies();
    } catch (err) {
      console.error(err);
    }
  };

  /* ---------------- CREATE ---------------- */

  const handleCreateCompany = async () => {
    try {
      setLoading(true);
      const res = await axios.post(
        `${process.env.REACT_APP_OCPI_URL}/v1/ocpi/dashboard/parties/`,
        addData,
        config,
      );

      setOpenAdd(false);
      setAddData(emptyCompany);
      await fetchCompanies();

      if (res?.data?.data) {
        setSelectedCompany(res.data.data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleLoadCompany = async (id) => {
    try {
      // COMPANY DETAILS
      setLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_OCPI_URL}/v1/ocpi/dashboard/party/${id}/`,
        config,
      );

      if (res?.data?.data) {
        setSelectedCompany(res.data.data);
      }

      /* ================= LOCATIONS ================= */

      const locRes = await axios.get(
        `${process.env.REACT_APP_OCPI_URL}/v1/ocpi/dashboard/party/${id}/locations/`,
        config,
      );

      if (locRes?.data?.data?.what_we_shared) {
        setLocationsShared(locRes.data.data.what_we_shared);
      } else {
        setLocationsShared([]);
      }

      if (locRes?.data?.data?.what_we_received) {
        setLocationsReceived(locRes.data.data.what_we_received);
      } else {
        setLocationsReceived([]);
      }

      /* ================= SESSIONS ================= */

      const sessionRes = await axios.get(
        `${process.env.REACT_APP_OCPI_URL}/v1/ocpi/dashboard/party/${id}/sessions/`,
        config,
      );

      if (sessionRes?.data?.data?.what_we_started) {
        setSessionsShared(sessionRes.data.data.what_we_started);
      } else {
        setSessionsShared([]);
      }
      if (sessionRes?.data?.data?.what_they_started) {
        setSessionsReceived(sessionRes.data.data.what_they_started);
      } else {
        setSessionsReceived([]);
      }

      /* ================= CDRs ================= */

      const cdrRes = await axios.get(
        `${process.env.REACT_APP_OCPI_URL}/v1/ocpi/dashboard/party/${id}/cdrs/`,
        config,
      );

      if (cdrRes?.data?.data?.what_we_started) {
        setCdrShared(cdrRes.data.data.what_we_started);
      } else {
        setCdrShared([]);
      }
      if (cdrRes?.data?.data?.what_they_started) {
        setCdrReceived(cdrRes.data.data.what_they_started);
      } else {
        setCdrReceived([]);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleStartCharging = async (evse) => {
    try {
      setLoading(true);
      const chargerIdentifier = `${evse.uid}__${evse.connectors[0]?.id}`;

      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}charger/start/${chargerIdentifier}/`,
        { time: 60 },
        {
          headers: {
            Authorization: `Bearer 6bxyl6ASRc8jwbu7tl1rCEFKt4vAkmpIxUqoD8LsxxasfQ4oB2QNlUUcjNSUUjSA`,
            "Content-Type": "application/json",
          },
        },
      );

      if (response.data?.success) {
        const sessionId = response.data.data.session_id;

        setActiveSessions((prev) => ({
          ...prev,
          [evse.evse_id]: sessionId,
        }));

        showToast("Charging started successfully!", "success");
      } else {
        showToast("Failed to start charging", "error");
      }
    } catch (error) {
      console.error("Start Charging Error:", error);
      showToast("Error starting charging", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleStopCharging = async (evse) => {
    try {
      const sessionId = activeSessions[evse.evse_id];

      if (!sessionId) {
        showToast("No active session found for this EVSE", "error");
        return;
      }
      setLoading(true);
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}charger/stop/${sessionId}/`,
        {},
        {
          headers: {
            Authorization: `Bearer 6bxyl6ASRc8jwbu7tl1rCEFKt4vAkmpIxUqoD8LsxxasfQ4oB2QNlUUcjNSUUjSA`,
          },
        },
      );

      if (response.data?.success) {
        showToast("Stop request sent successfully!", "success");

        // Remove session after stop trigger
        setActiveSessions((prev) => {
          const updated = { ...prev };
          delete updated[evse.evse_id];
          return updated;
        });
      } else {
        showToast("Failed to stop charging", "error");
      }
    } catch (error) {
      console.error("Stop Charging Error:", error);
      showToast("Error stopping charging", "error");
    } finally {
      setLoading(false);
    }
  };

  const renderEVSEs = (evses, type) => {
    if (!evses || evses.length === 0) return "No EVSE";

    return evses.map((evse, index) => (
      <Accordion key={evse.uid || index} sx={{ mb: 1 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">
            EVSE: {evse.evse_id} ({evse.status})
          </Typography>
        </AccordionSummary>

        <AccordionDetails>
          {type === "received" && (
            <Box sx={{ mb: 2 }}>
              <Button
                size="small"
                variant="contained"
                sx={{ mr: 1 }}
                onClick={() => handleStartCharging(evse)}
              >
                Start Charging
              </Button>

              <Button
                size="small"
                variant="outlined"
                color="error"
                onClick={() => handleStopCharging(evse)}
              >
                Stop Charging
              </Button>
            </Box>
          )}

          {/* EVSE basic info */}
          <Typography variant="caption">UID: {evse.uid}</Typography>
          <br />
          <Typography variant="caption">Floor: {evse.floor_level}</Typography>

          {/* Connectors */}
          {evse.connectors && evse.connectors.length > 0 && (
            <>
              <Typography sx={{ mt: 2 }} fontWeight="bold">
                Connectors
              </Typography>

              {evse.connectors.map((connector, i) => (
                <Accordion key={connector.id || i} sx={{ mb: 1 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="body2">
                      Connector: {connector.id}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    {Object.entries(connector).map(([key, val]) => (
                      <Typography key={key} variant="caption" display="block">
                        {key}: {String(val)}
                      </Typography>
                    ))}
                  </AccordionDetails>
                </Accordion>
              ))}
            </>
          )}
        </AccordionDetails>
      </Accordion>
    ));
  };

  const renderTable = (
    data,
    type,
    identifier = "locations",
    enableSelection = false,
    selectedItems = [],
    onSelectItem = null,
    onSelectAll = null,
    onPushItem = null,
  ) => {
    if (!data || data.length === 0) {
      return (
        <Typography align="center" sx={{ mt: 2 }}>
          No Data
        </Typography>
      );
    }

    // For locations in "what we shared" tab - use the original logic
    const isLocationShared = type === "shared" && identifier === "locations";

    // For CDRs and Sessions in "what they started" tab - use the new selection logic
    const isCDROrSessionReceived =
      (identifier === "cdrs" || identifier === "sessions") &&
      type === "received" &&
      enableSelection;

    return (
      <Box sx={{ overflowX: "auto" }}>
        {/* Bulk push button for locations */}
        {isLocationShared && (
          <Box
            sx={{
              mb: 2,
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Button
              variant="contained"
              disabled={selectedLocations.length === 0}
              onClick={() => setOpenPushAllDialog(true)}
            >
              {selectedLocations.length === locationsShared.length
                ? "Push All"
                : "Push Selected"}
            </Button>

            {bulkPushLoading && (
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption">
                  {bulkPushProgress.completed}/{bulkPushProgress.total}
                </Typography>

                <LinearProgress
                  variant="determinate"
                  value={
                    bulkPushProgress.total > 0
                      ? (bulkPushProgress.completed / bulkPushProgress.total) *
                        100
                      : 0
                  }
                />
              </Box>
            )}
          </Box>
        )}

        {/* Bulk push button for CDRs/Sessions */}
        {isCDROrSessionReceived && (
          <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 2 }}>
            <Button
              variant="contained"
              disabled={selectedItems.length === 0}
              onClick={() => {
                selectedItems.forEach((itemId) => {
                  const itemToPush = data.find((d) => d.id === itemId);
                  if (itemToPush) onPushItem(itemToPush);
                });
              }}
            >
              Push Selected ({selectedItems.length})
            </Button>
          </Box>
        )}

        <table
          width="100%"
          cellPadding="10"
          style={{ borderCollapse: "collapse" }}
        >
          <thead>
            <tr>
              {/* Selection column for locations */}
              {isLocationShared && (
                <th style={{ borderBottom: "1px solid #ddd" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Checkbox
                      checked={
                        locationsShared.length > 0 &&
                        selectedLocations.length === locationsShared.length
                      }
                      onChange={handleSelectAllLocations}
                    />
                    Action
                  </Box>
                </th>
              )}

              {/* Selection column for CDRs/Sessions */}
              {isCDROrSessionReceived && (
                <th style={{ borderBottom: "1px solid #ddd" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Checkbox
                      checked={
                        data.length > 0 && selectedItems.length === data.length
                      }
                      onChange={onSelectAll}
                    />
                    Action
                  </Box>
                </th>
              )}

              {/* Data columns */}
              {Object.keys(data[0]).map((key) => (
                <th
                  key={key}
                  style={{
                    borderBottom: "1px solid #ddd",
                    textAlign: "left",
                  }}
                >
                  {key}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {data.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {/* Action buttons for locations */}
                {isLocationShared && (
                  <td style={{ borderBottom: "1px solid #eee" }}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Checkbox
                        checked={selectedLocations.includes(row.id)}
                        onChange={() => handleSelectLocation(row.id)}
                      />
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => handlePushLocation(row)}
                      >
                        Push
                      </Button>
                    </Box>
                  </td>
                )}

                {/* Action buttons for CDRs/Sessions */}
                {isCDROrSessionReceived && (
                  <td style={{ borderBottom: "1px solid #eee" }}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Checkbox
                        checked={selectedItems.includes(row.id)}
                        onChange={() => onSelectItem(row.id)}
                      />
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => onPushItem(row)}
                      >
                        Push
                      </Button>
                    </Box>
                  </td>
                )}

                {/* Data cells */}
                {Object.entries(row).map(([key, value]) => (
                  <td
                    key={key}
                    style={{
                      borderBottom: "1px solid #eee",
                      verticalAlign: "top",
                    }}
                  >
                    {key === "evses" ? (
                      renderEVSEs(value, type)
                    ) : typeof value === "object" ? (
                      <pre style={{ margin: 0 }}>
                        {JSON.stringify(value, null, 2)}
                      </pre>
                    ) : (
                      String(value)
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </Box>
    );
  };

  /* ================= UI ================= */

  return (
    <>
      {loading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0,0,0,0.4)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress size={60} />
        </Box>
      )}

      <Grid container spacing={3} sx={{ mt: 1 }}>
        {/* LEFT PANEL */}
        <Grid item xs={3}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6">Companies</Typography>

            <TextField
              fullWidth
              size="small"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ my: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

            <List>
              {filteredCompanies.map((company) => (
                <ListItemButton
                  key={company.id}
                  selected={selectedCompany?.id === company.id}
                  onClick={() => handleLoadCompany(company.id)}
                  sx={{ display: "flex", justifyContent: "space-between" }}
                >
                  <ListItemText primary={company.name} />

                  <IconButton
                    onClick={(e) => handleMenuOpen(e, company.id)}
                    size="small"
                  >
                    <MoreVertIcon />
                  </IconButton>
                </ListItemButton>
              ))}
            </List>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
            >
              <MenuItem onClick={handleDelete}>Delete</MenuItem>
            </Menu>
            {/* ADD */}
            <Tooltip title="Add OCPI Party" arrow>
              <Fab
                color="secondary"
                sx={{ position: "absolute", left: 120, bottom: 10 }}
                onClick={() => setOpenAdd(true)}
              >
                <AddIcon />
              </Fab>
            </Tooltip>
          </Paper>
        </Grid>

        {/* RIGHT PANEL */}
        <Grid item xs={9}>
          {selectedCompany && (
            <Paper sx={{ p: 3, position: "relative" }}>
              <Typography variant="h5">{selectedCompany.name}</Typography>

              {/* <Divider sx={{ my: 2 }} /> */}

              {/* MAIN TABS */}
              <Tabs value={detailTab} onChange={(e, v) => setDetailTab(v)}>
                <Tab label="Details" />
                <Tab label="Locations" />
                <Tab label="Tariffs" />
                <Tab label="Endpoints" />
                <Tab label="Tokens" />
                <Tab label="Handshake" />
                <Tab label="CDRs" />
                <Tab label="Sessions" />
                <Tab label="Logs" />
              </Tabs>

              <TabPanel value={detailTab} index={0}>
                <pre>{JSON.stringify(selectedCompany, null, 2)}</pre>
              </TabPanel>

              {/* Locations Tab */}
              <TabPanel value={detailTab} index={1}>
                <Tabs
                  value={locationTab}
                  onChange={(e, v) => setLocationTab(v)}
                >
                  <Tab label="What we shared" />
                  <Tab label="What they shared" />
                </Tabs>
                <NestedTabPanel value={locationTab} index={0}>
                  {renderTable(locationsShared, "shared", "locations")}
                </NestedTabPanel>
                <NestedTabPanel value={locationTab} index={1}>
                  {renderTable(locationsReceived, "received", "locations")}
                </NestedTabPanel>
              </TabPanel>

              <TabPanel value={detailTab} index={2}>
                <Tabs value={tariffTab} onChange={(e, v) => setTariffTab(v)}>
                  <Tab label="What we shared" />
                  <Tab label="What they shared" />
                </Tabs>
                <NestedTabPanel value={tariffTab} index={0}>
                  {renderTable(tariffsShared, "shared", "tariffs")}
                </NestedTabPanel>
                <NestedTabPanel value={tariffTab} index={1}>
                  {renderTable(tariffsReceived, "received", "tariffs")}
                </NestedTabPanel>
              </TabPanel>

              <TabPanel value={detailTab} index={3}>
                <Box sx={{ mb: 2 }}>
                  <Button
                    variant="contained"
                    onClick={handleFetchEndpoints}
                    disabled={!selectedCompany?.is_handshake_done}
                  >
                    Fetch Endpoints
                  </Button>
                </Box>

                <Box sx={{ mb: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Button
                    onClick={() =>
                      handleFetchOCPIEndpoint("locations", "SENDER")
                    }
                  >
                    Locations
                  </Button>
                  <Button
                    onClick={() => handleFetchOCPIEndpoint("tariffs", "SENDER")}
                  >
                    Tariffs
                  </Button>
                  <Button
                    onClick={() =>
                      handleFetchOCPIEndpoint("sessions", "SENDER")
                    }
                  >
                    Sessions
                  </Button>
                  <Button
                    onClick={() => handleFetchOCPIEndpoint("cdrs", "SENDER")}
                  >
                    CDRs
                  </Button>
                </Box>

                <pre>
                  {JSON.stringify(selectedCompany.all_endpoints || {}, null, 2)}
                </pre>
              </TabPanel>

              <TabPanel value={detailTab} index={4}>
                <pre>
                  {JSON.stringify(
                    {
                      token_a: selectedCompany.token_a,
                      token_b: selectedCompany.token_b,
                      token_c: selectedCompany.token_c,
                    },
                    null,
                    2,
                  )}
                </pre>
              </TabPanel>

              <TabPanel value={detailTab} index={5}>
                <Box sx={{ mb: 2 }}>
                  <Button
                    variant="contained"
                    onClick={handleHandshake}
                    disabled={handshakeLoading}
                  >
                    {handshakeLoading
                      ? "Processing..."
                      : selectedCompany.is_handshake_done
                        ? "Update Handshake"
                        : "Do Handshake"}
                  </Button>
                </Box>

                <pre>
                  {JSON.stringify(
                    {
                      version: selectedCompany.version,
                      handshake_endpoint: selectedCompany.handshake_endpoint,
                      is_handshake_done: selectedCompany.is_handshake_done,
                    },
                    null,
                    2,
                  )}
                </pre>
              </TabPanel>

              {/* CDRs Tab */}
              <TabPanel value={detailTab} index={6}>
                <Tabs value={cdrTab} onChange={(e, v) => setCdrTab(v)}>
                  <Tab label="What we started" />
                  <Tab label="What they started" />
                </Tabs>
                <NestedTabPanel value={cdrTab} index={0}>
                  {renderTable(cdrShared, "shared", "cdrs")}
                </NestedTabPanel>
                <NestedTabPanel value={cdrTab} index={1}>
                  {renderTable(
                    cdrReceived,
                    "received",
                    "cdrs",
                    true,
                    selectedCDRs,
                    handleSelectCDR,
                    handleSelectAllCDRs,
                    handlePushCDR,
                  )}
                </NestedTabPanel>
              </TabPanel>

              {/* Sessions Tab */}
              <TabPanel value={detailTab} index={7}>
                <Tabs value={sessionTab} onChange={(e, v) => setSessionTab(v)}>
                  <Tab label="What we started" />
                  <Tab label="What they started" />
                </Tabs>
                <NestedTabPanel value={sessionTab} index={0}>
                  {renderTable(sessionsShared, "shared", "sessions")}
                </NestedTabPanel>
                <NestedTabPanel value={sessionTab} index={1}>
                  {renderTable(
                    sessionsReceived,
                    "received",
                    "sessions",
                    true,
                    selectedSessions,
                    handleSelectSession,
                    handleSelectAllSessions,
                    handlePushSession,
                  )}
                </NestedTabPanel>
              </TabPanel>
              <TabPanel value={detailTab} index={8}>
                <pre>Logs</pre>
              </TabPanel>
            </Paper>
          )}
        </Grid>
      </Grid>

      {/* CREATE DIALOG */}
      <Dialog
        open={openAdd}
        onClose={() => setOpenAdd(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Create Company</DialogTitle>
        <DialogContent dividers>
          {Object.keys(emptyCompany).map((field) =>
            typeof emptyCompany[field] === "boolean" ? (
              <FormControlLabel
                key={field}
                control={
                  <Switch
                    checked={addData[field]}
                    onChange={(e) =>
                      setAddData({ ...addData, [field]: e.target.checked })
                    }
                  />
                }
                label={field}
              />
            ) : (
              <TextField
                key={field}
                fullWidth
                margin="normal"
                label={field}
                value={addData[field]}
                onChange={(e) =>
                  setAddData({ ...addData, [field]: e.target.value })
                }
              />
            ),
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAdd(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateCompany}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* EDIT DIALOG */}
      <Dialog
        open={openEdit}
        onClose={() => setOpenEdit(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Company</DialogTitle>
        <DialogContent dividers>
          {Object.keys(emptyCompany).map((field) =>
            typeof emptyCompany[field] === "boolean" ? (
              <FormControlLabel
                key={field}
                control={
                  <Switch
                    checked={editData[field] || false}
                    onChange={(e) =>
                      setEditData({ ...editData, [field]: e.target.checked })
                    }
                  />
                }
                label={field}
              />
            ) : (
              <TextField
                key={field}
                fullWidth
                margin="normal"
                label={field}
                value={editData[field] || ""}
                onChange={(e) =>
                  setEditData({ ...editData, [field]: e.target.value })
                }
              />
            ),
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEdit(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdate}>
            Update
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openHandshakeDialog}
        onClose={() => setOpenHandshakeDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>OCPI Handshake</DialogTitle>

        <DialogContent dividers>
          <TextField
            fullWidth
            margin="normal"
            label="Handshake Endpoint (Credentials URL)"
            value={handshakeForm.endpoint}
            onChange={(e) =>
              setHandshakeForm({
                ...handshakeForm,
                endpoint: e.target.value,
              })
            }
            InputProps={{
              readOnly: selectedCompany?.is_handshake_done,
            }}
          />

          {!selectedCompany?.is_handshake_done && (
            <TextField
              fullWidth
              margin="normal"
              label="Authorization Token (Token A)"
              value={handshakeForm.auth_token}
              onChange={(e) =>
                setHandshakeForm({
                  ...handshakeForm,
                  auth_token: e.target.value,
                })
              }
            />
          )}
          <TextField
            fullWidth
            margin="normal"
            label="Your OCPI URL (Callback URL)"
            value={handshakeForm.url}
            // onChange={(e) =>
            //   setHandshakeForm({
            //     ...handshakeForm,
            //     url: e.target.value,
            //   })
            // }
            InputProps={{
              readOnly: selectedCompany?.is_handshake_done,
            }}
          />
          <TextField
            fullWidth
            margin="normal"
            label="Token (UUID)"
            value={handshakeForm.payload_token}
            onChange={(e) => {
              const value = e.target.value;

              setHandshakeForm((prev) => ({
                ...prev,
                payload_token: value,
              }));
              setIsTokenSaved(false);
            }}
            InputProps={{
              readOnly: !selectedCompany?.is_handshake_done, // 👈 key logic

              endAdornment: selectedCompany?.is_handshake_done && (
                <InputAdornment position="end">
                  <Button
                    size="small"
                    variant="outlined"
                    sx={{ mr: 1 }}
                    onClick={() => {
                      setHandshakeForm((prev) => ({
                        ...prev,
                        payload_token: crypto.randomUUID(),
                      }));
                      setIsTokenSaved(false);
                    }}
                  >
                    Generate
                  </Button>

                  <Button
                    size="small"
                    variant="contained"
                    disabled={savingToken || !handshakeForm.payload_token}
                    onClick={handleSaveTokenC}
                  >
                    {savingToken ? "Saving..." : "Save"}
                  </Button>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            margin="normal"
            label="Party ID"
            value={handshakeForm.party_id}
            onChange={(e) =>
              setHandshakeForm({ ...handshakeForm, party_id: e.target.value })
            }
            InputProps={{
              readOnly: selectedCompany?.is_handshake_done,
            }}
          />

          <TextField
            fullWidth
            margin="normal"
            label="Country Code"
            value={handshakeForm.country_code}
            onChange={(e) =>
              setHandshakeForm({
                ...handshakeForm,
                country_code: e.target.value,
              })
            }
            InputProps={{
              readOnly: selectedCompany?.is_handshake_done,
            }}
          />

          <TextField
            fullWidth
            margin="normal"
            label="Business Name"
            value={handshakeForm.name}
            onChange={(e) =>
              setHandshakeForm({ ...handshakeForm, name: e.target.value })
            }
            InputProps={{
              readOnly: selectedCompany?.is_handshake_done,
            }}
          />

          <FormControlLabel
            control={
              <Switch
                checked={handshakeForm.is_cpo}
                onChange={(e) =>
                  setHandshakeForm({
                    ...handshakeForm,
                    is_cpo: e.target.checked,
                  })
                }
              />
            }
            label="CPO"
          />

          <FormControlLabel
            control={
              <Switch
                checked={handshakeForm.is_emsp}
                onChange={(e) =>
                  setHandshakeForm({
                    ...handshakeForm,
                    is_emsp: e.target.checked,
                  })
                }
              />
            }
            label="EMSP"
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenHandshakeDialog(false)}>Cancel</Button>

          <Button
            variant="contained"
            disabled={
              handshakeLoading ||
              (selectedCompany?.is_handshake_done && !isTokenSaved)
            }
            onClick={() => {
              executeHandshake({
                endpoint: handshakeForm.endpoint,
                isUpdate: selectedCompany?.is_handshake_done,
              });
            }}
          >
            {handshakeLoading ? "Processing..." : "Start Handshake"}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openEndpointModal}
        onClose={() => setOpenEndpointModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{endpointTitle} Data</DialogTitle>

        <DialogContent dividers>
          <pre style={{ whiteSpace: "pre-wrap" }}>
            {JSON.stringify(endpointData, null, 2)}
          </pre>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenEndpointModal(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openPushAllDialog}
        onClose={() => setOpenPushAllDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delay in Seconds</DialogTitle>

        <DialogContent dividers>
          <TextField
            fullWidth
            type="number"
            label="Delay"
            value={pushDelay}
            onChange={(e) => setPushDelay(Number(e.target.value))}
            inputProps={{
              min: 1,
            }}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenPushAllDialog(false)}>Cancel</Button>

          <Button
            variant="contained"
            disabled={bulkPushLoading}
            onClick={handlePushAllLocations}
          >
            Start Push
          </Button>
        </DialogActions>
      </Dialog>
      {/* Toast Component */}
      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseToast}
          severity={toast.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default OCPICompaniesPage;
